import React from "react";
// Billing Summary Page

// Default
import { useState, useEffect } from "react";
import moment from "moment";
import "moment-timezone";

// Mui
import Card from "@mui/material/Card";
import Typography from "@mui/material/Typography";
import CardContent from "@mui/material/CardContent";
import CardHeader from "@mui/material/CardHeader";
import Divider from "@mui/material/Divider";
import Stack from "@mui/material/Stack";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";

// Custom
import Layout from "../../layout";
import ContainedButton from "../../forms/button/ContainedButton";
import CustomDataTable from "../../components/table/CustomTable";
import BillingSummaryPieChart from "../charts/BillingSummaryPieChart";

import CustomSearch from "../search/Search";
import { formatTime, formatDate } from "../../utils/helper";
import Calendar2 from "../../assets/svg/calendar2";
import DateAndTimePopover from "../popovers/DateAndTimePopover";
import BillingSummaryReportLine from "../charts/BillingSummaryReportLine";

// Types
interface BillingSummaryData {
  consumerName: string;
  registerType: string;
  status: string;
  amount: number;
  date: string;
  [key: string]: any;
}

interface TimeStamp {
  start: number;
  end: number;
}

const startOfDayIST = moment().tz("Asia/Kolkata").startOf("day").valueOf();
const endOfDayIST = moment().tz("Asia/Kolkata").endOf("day").valueOf();

const getTimeStamp = (newTime: string, newDate: Date): number => {
  const [hours, minutes] = newTime.split(":").map(Number);

  // Create a moment object in IST using newDate
  const istMoment = moment
    .tz(newDate, "Asia/Kolkata")
    .set({ hour: hours, minute: minutes, second: 0, millisecond: 0 });

  return istMoment.valueOf(); // Returns UTC timestamp representing the selection in IST
};

const BillingSummary: React.FC = () => {
  const [summaryData, setSummaryData] = useState<BillingSummaryData[]>([]);
  const [maxName, setMaxName] = useState<string>("");
  const [maxValue, setMaxValue] = useState<number>(0);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const [endDate, setEndDate] = useState<Date>(new Date());
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [timeRange, setTimeRange] = useState<number[]>([0, 1439]);
  const [timeStamp, setTimeStamp] = useState<TimeStamp>({
    start: startOfDayIST,
    end: endOfDayIST,
  });

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleTimeRangeChange = (timeRange: number | number[]) => {
    if (Array.isArray(timeRange)) {
      setTimeRange(timeRange);
    } else {
      setTimeRange([timeRange, timeRange]);
    }
  };

  const handleDateChange = (dates: [Date, Date]) => {
    const [start, end] = dates;
    setStartDate(start);
    setEndDate(end);
  };

  const handleDateSubmit = () => {
    const startTs = getTimeStamp(formatTime(timeRange[0]), new Date(startDate));
    const endTs = getTimeStamp(formatTime(timeRange[1]), new Date(endDate));

    setTimeStamp({
      start: startTs,
      end: endTs,
    });

    setAnchorEl(null);
  };

  const generateRandomConsumerName = () => {
    const names = [
      "John Doe",
      "Jane Smith",
      "Robert Johnson",
      "Emily Davis",
      "Michael Brown",
    ];
    return names[Math.floor(Math.random() * names.length)];
  };

  const generateRandomData = () => {
    const statuses = ["Hold", "Processed", "Failed"];
    const data = [];

    for (let i = 1; i <= 20; i++) {
      data.push({
        consumerName: generateRandomConsumerName(),
        registerType: "IP_KWH",
        status: statuses[Math.floor(Math.random() * statuses.length)],
        amount: Math.floor(Math.random() * 500) + 50,
        date: new Date().toISOString().substring(0, 10),
      });
    }

    return data;
  };

  useEffect(() => {
    const data = [
      {
        consumerName: "Ramesh Kumar",
        registerType: "Energy",
        status: "Active",
        amount: 1500,
        date: "2023-01-15",
      },
      {
        consumerName: "Priya Sharma",
        registerType: "Demand",
        status: "Inactive",
        amount: 2200,
        date: "2023-01-20",
      },
      {
        consumerName: "Amit Patel",
        registerType: "Energy",
        status: "Active",
        amount: 1800,
        date: "2023-01-25",
      },
      {
        consumerName: "Sunita Verma",
        registerType: "Demand",
        status: "Active",
        amount: 3200,
        date: "2023-02-01",
      },
      {
        consumerName: "Rajesh Singh",
        registerType: "Energy",
        status: "Active",
        amount: 2800,
        date: "2023-02-10",
      },
    ];

    setSummaryData(data);

    const counts = data.reduce((acc: Record<string, number>, item) => {
      acc[item.status] = (acc[item.status] || 0) + 1;
      return acc;
    }, {});

    const entries = Object.entries(counts);
    const maxStatus = entries.reduce(
      (max, current) => (current[1] > max[1] ? current : max),
      ["", 0] as [string, number],
    );

    setMaxValue(Number(maxStatus[1]));
    setMaxName(String(maxStatus[0]));
  }, []);

  console.log(summaryData, "----");

  const COLUMNS = [
    "Consumer Name",
    "Register Type",
    "Status",
    "Amount",
    "Date",
  ];

  const rowsContent = summaryData?.map((item, index) => (
    <TableRow
      key={index}
      sx={{
        height: "50px",
        background: (index + 1) % 2 ? "#F9FCFE" : "transparent",
      }}
      className="custom_table_tbody"
    >
      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.consumerName}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.registerType}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.status}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">
          {item.amount}
        </Typography>
      </TableCell>

      <TableCell>
        <Typography className="custom_table_tbody_cell">{item.date}</Typography>
      </TableCell>
    </TableRow>
  ));

  return (
    <Layout>
      {/* <Card className="meters_title_card">
        <Typography variant="h5">Billing Summary</Typography>
      </Card> */}

      <Card sx={{ mt: "20px", maxHeight: "400px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5">Billing Summary</Typography>
              <Stack direction={"row"} alignItems={"center"}>
                <Box mr={"20px"}>
                  <CustomSearch
                    className={"meter-command_report_top_card_search"}
                    placeholder={"Enter Meter Id"}
                    isIcon={false}
                  />
                </Box>
                <Box
                  sx={{
                    fontWeight: 600,
                    letterSpacing: "0.32px",
                    padding: "1px 8px",
                    color: "#ffffff",
                    width: "fit-content",
                  }}
                >
                  <Stack
                    direction={"row"}
                    justifyContent={"center"}
                    gap={"5px"}
                    alignItems={"center"}
                    px={"5px"}
                  >
                    <Typography>
                      {formatDate(startDate, "D MMMM YYYY")} -{" "}
                    </Typography>
                    <Typography>
                      {formatDate(endDate, "D MMMM YYYY")}
                    </Typography>
                  </Stack>
                </Box>
                <IconButton
                  onClick={(event) => setAnchorEl(event.currentTarget)}
                >
                  <Calendar2 />
                </IconButton>
                <Box
                  sx={{
                    width: "1px",
                    height: "31px",
                    background: "#CACDD7",
                    margin: "0px 20px",
                  }}
                ></Box>
                {/* <DatePickerInput
                  placeholder={"dd/mm/yyyy"}
                  width={"fit-content"}
                />
                <DatePickerInput
                  placeholder={"dd/mm/yyyy"}
                  width={"fit-content"}
                /> */}
                <ContainedButton
                  label={"Apply Filters"}
                  sx={{
                    width: "fit-content",
                    color: "#ffffff",
                    boxShadow: "0",
                  }}
                />
              </Stack>
            </Stack>
          }
        />
        <Divider />

        <CardContent>
          <Stack
            flex={2}
            flexDirection={"row"}
            gap={4}
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Stack flex={1}>
              <BillingSummaryReportLine />
            </Stack>
            <Stack flex={1}>
              <BillingSummaryPieChart
                summaryData={summaryData}
                maxName={maxName}
                maxValue={maxValue}
              />
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mt: "20px" }}>
        <CardHeader
          title={
            <Stack
              direction={"row"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <Typography variant="h5">Billing Summary Details</Typography>
              <ContainedButton
                label={"Download CSV"}
                sx={{
                  width: "fit-content",
                  color: "#ffffff",
                  boxShadow: "0",
                }}
                // onClick={downloadCSV}
              />
            </Stack>
          }
        />
        <Divider />
        <CardContent
          className="no-scrollbar"
          sx={{ overflow: "auto", maxHeight: "600px", padding: "0" }}
        >
          <CustomDataTable
            columns={COLUMNS}
            pagination={true}
            rowsContent={rowsContent}
            Actions={true}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            page={page}
            onRowsPerPageChange={handleChangeRowsPerPage}
            totalCount={summaryData?.length}
          />
        </CardContent>
      </Card>

      <DateAndTimePopover
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        startDate={startDate}
        endDate={endDate}
        handleDateChange={handleDateChange}
        timeRange={timeRange}
        setTimeRange={handleTimeRangeChange}
        handleDateSubmit={handleDateSubmit}
      />
    </Layout>
  );
};

export default BillingSummary;
